import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { or, readOnly } from '@ember/object/computed';
import { dropTask, enqueueTask, task } from 'ember-concurrency';
import { showModal } from 'sh-www/utils/modal-controls';
import { inject as service } from '@ember/service';
import { CLASSIFICATION_TYPES } from 'sh-www/models/classification';
import LightboxOverlay from 'sh-www/components/modals/overlays/lightbox';
import { isUrl } from 'sh-www/utils/misc';
import clsx from 'clsx';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { getClassificationIdsFromObject, mapClassificationsToCategories } from 'sh-www/helpers/admin/map-classifications-to-categories';

import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { USER } from 'sh-www/models/user';
import { USERS_LIST_COMPONENT } from 'sh-www/components/users-list';
import { INPUT_FIELDS } from 'sh-www/components/lsg/lsg-decimal-input';
import { VET_USER_INCLUDES } from 'sh-www/routes/admin/user/rate';

const VETTING_STATUS_NEEDS_VETTING = 3;

const CLASS_NAME = 'SH-r-admin-user-rate';

class AdminUserRateController extends Controller {
  @service moment;
  @service router;
  @service session;
  @service shData;
  @service store;
  @service('state-managers/application') applicationStateManager;
  @service('error-tracker') errorTracker;
  @service('commands/note-commands') noteCommandService;
  @service('commands/projectdigest-commands') projectDigestCommandService;
  @service('commands/classification-commands') classificationCommandService;
  @service('commands/user-commands') userCommandService;
  @service('video-thumbnail-from-url') videoThumbnailService;

  /** @var {Array} adminAddedUnlistedServices unlisted services displayed in UI */
  adminAddedUnlistedServices = [];

  /** @var {Array} */
  projects = [];

  /** @var {Object} */
  pagination = null;

  /** @var {Service} */
  selectedUnlistedService = null;

  /** @var {Object} */
  serviceRatings = {};

  /** @var {Object} */
  serviceRatingOverrides = {};

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  @readOnly('loadProjectsWaiter.isRunning')
  isLoadingWorkSamples;

  @computed('loadClassificationsTask.lastSuccessful.value')
  get classificationItems() {
    return mapClassificationsToCategories(this.loadClassificationsTask.lastSuccessful?.value);
  }

  @computed('model.user.{id,fullName}')
  get userName() {
    return `${this.model.user.fullName} (${this.model.user.id})`;
  }

  @computed('model.user.approval')
  get userMenuOptions () {
    const { user } = this.model;
    const menuActions = [
      USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED,
    ];

    const menuOptions = [];
    menuActions.forEach((menuAction) => {
      const menuOption = this.menuOptionForUser(user, menuAction);
      if (menuOption) {
        menuOptions.addObject(menuOption);
      }
    });

    return menuOptions;
  }

  @or('invokeMenu.isRunning','reloadUser.isRunning')
  disableUserMenuActions;

  @computed('finalServiceRatings','saveServiceRatingsTask.isRunning','reloadUser.isRunning')
  get disableSaveAction() {
    return Object.keys(this.finalServiceRatings).length === 0
      || this.saveServiceRatingsTask.isRunning
      || this.reloadUser.isRunning;
  }

  /**
   * @returns {array}
   */
  @computed('projects.[]')
  get supportedListedServices() {
    const workSampleServices = new Set();

    // collect all services listed on each work sample
    this.projects.forEach(project => {
      project.services.forEach(service => workSampleServices.add(service));
    });

    return Array.from(workSampleServices);
  }

  /**
   * @returns {array}
   */
  @computed('supportedListedServices.[]','model.user.services.[]')
  get unsupportedListedServices() {
    const supportedListedServicesIds = this.supportedListedServices.map(({id}) => id);

    // return all services listed without work sample support
    return this.model.user.services
      .filter(service => !supportedListedServicesIds.includes(service.id));
  }

  /**
   * @returns {array}
   */
  @computed('model.allServices.[]','supportedListedServices.[]','unsupportedListedServices.[]')
  get unlistedServices() {
    const { allServices } = this.model;
    const supportedListedServicesIds = this.supportedListedServices.map(({id}) => id);
    const unsupportedListedServicesIds = this.unsupportedListedServices.map(({id}) => id);

    const listedServiceIds = [
      ...supportedListedServicesIds,
      ...unsupportedListedServicesIds,
    ];

    return allServices.filter(service => !listedServiceIds.includes(service.id));
  }

  /**
   * @returns {string}
   */
  @computed('adminAddedUnlistedServices.[],unlistedServices.[]')
  get unlistedServiceSelectOptions() {
    // remove services from the list after being added as a rated service
    const adminAddedUnlistedServiceIds = this.adminAddedUnlistedServices.map(({id}) => id);
    const remainingUnlistedServices = [...this.unlistedServices].filter(({id}) => {
      return !adminAddedUnlistedServiceIds.includes(id);
    });

    return [
      {
        key: 'Select an Option',
        model: null,
      },
      ...remainingUnlistedServices.map(service => ({
        key: service.name,
        model: service,
      })),
    ];
  }

  /**
   * Get the calculated average rating of each service
   */
  @computed('serviceRatings')
  get calculatedServiceRatings() {
    const { serviceRatings } = this;

    const individualScores = Object.keys(serviceRatings).reduce((accum, key) => {
      const separatorIndex = key.indexOf('-');
      const serviceId = separatorIndex === -1 ? key : key.substring(0, separatorIndex);

      const inputValues = serviceRatings[key];
      const whole = inputValues[INPUT_FIELDS.WHOLE];
      const decimal = inputValues[INPUT_FIELDS.DECIMAL] ?? 0;

      if (whole) {
        const score = Number(`${whole}.${decimal}`);

        // separate rating by those of same service and store within array
        if (!accum[serviceId]) accum[serviceId] = [];
        accum[serviceId].push(score);
      }

      return accum;
    }, {});

    return Object.keys(individualScores).reduce((accum, serviceId) => {
      const sum = individualScores[serviceId].reduce((a, b) => a + b, 0);
      const avg = sum / individualScores[serviceId].length;
      accum[serviceId] = Number(avg.toFixed(1));

      return accum;
    }, {});
  }

  /**
   * Get the final calculated service ratings
   */
  @computed('calculatedServiceRatings','serviceRatingOverrides')
  get finalServiceRatings() {
    const { serviceRatingOverrides } = this;

    const finalScores = { ...this.calculatedServiceRatings };

    return Object.keys(serviceRatingOverrides).reduce((accum, serviceId) => {
      const inputValues = serviceRatingOverrides[serviceId];

      // if an override was done to clear the service rating
      if (!inputValues[INPUT_FIELDS.WHOLE] && !inputValues[INPUT_FIELDS.DECIMAL]) {
        // set the final value to zero so that it is unrated
        accum[serviceId] = 0;

        return accum;
      }

      // if the decimal was not selected just default to original score's decimal
      const calculatedScore = finalScores[serviceId] ?? 0;
      const [whole, decimal] = calculatedScore.toFixed(1).split('.').map(Number);

      const wholePart = inputValues[INPUT_FIELDS.WHOLE] ?? whole;
      const decimalPart = inputValues[INPUT_FIELDS.DECIMAL] ?? decimal;

      // update any overridden scores
      accum[serviceId] = Number(`${wholePart}.${decimalPart}`);

      return accum;
    }, finalScores);
  }

  /** @type {Array<ServiceUserScore>} Any existing service scores */
  @computed('user.serviceUserScores.[]')
  get existingAdminScores() {
    const { serviceUserScores } = this.model.user;

    if (!serviceUserScores) {
      return [];
    }

    return serviceUserScores.filter((adminReviewScore) => adminReviewScore.ascore > 0);
  }

  menuOptionForUser(user, menuAction) {
    switch (menuAction) {
      case USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR:
        return {
          type: USERS_LIST_COMPONENT.MENU_ACTIONS.SEPARATOR,
        };
      case USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED: {
        let title;
        if (user.approval === USER.APPROVAL.UN_APPROVED) {
          title = 'Un-ban';
        } else if (user.approval === USER.APPROVAL.UNDEFINED) {
          title = 'Approve';
        } else {
          title = 'Ban';
        }

        return {
          title,
          onClick: () => {
            this.invokeMenu.perform(user, USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED);
          },
        };
      }
      default:
        return null;
    }
  }

  @task
  *loadProjectsWaiter() {
    try {
      yield this.loadProjects.perform(1);
      while (this.pagination.currentPage < this.pagination.totalPages) {
        yield this.loadProjects.perform(this.pagination.currentPage + 1);
      }
    } catch (e) {
      this.errorTracker.error(e, this, 'There was an error loading projects.');
      this.applicationStateManager.addToast("There was an error loading user's projects. Please reload the page and try again.");
    }
  }

  @task
  *loadProjects(pageToLoad) {
    const { user } = this.model;

    const response = yield this.userCommandService.getProjectDigests(
      user.id,
      ['classifications', 'company', 'oembed', 'services', 'projectdigest-verifications:verified_or_latest'],
      'created_at',
      pageToLoad,
      10
    );

    this.shData.pushResponseToStore(response);

    const pagination = this.shData.getPaginationModelFromResponse(response);
    const projects = this.shData.getModelArrayFromResponse(response, 'Projectdigest');

    this.set('pagination', pagination);
    this.projects.pushObjects(projects);

    const currentServiceRatings = { ...this.serviceRatings };
    const existingAdminScores = [ ...this.existingAdminScores ];

    // load existing scores if already vetted
    if (existingAdminScores.length) {
      const existingAdminScoreHash = existingAdminScores.reduce((accum, userServiceScore) => {
        accum[userServiceScore.service.id] = userServiceScore.ascore;
        return accum;
      }, {});

      const loadedDigestServiceRatings = projects.reduce((accum, projectDigest) => {
        // apply the saved rating as each service's default rating
        projectDigest.services.forEach((service) => {
          const score = existingAdminScoreHash[service.id];

          if (score) {
            // clean up any service scores that were loaded if they're related to a digest
            delete currentServiceRatings[service.id];

            const key = `${service.id}-${projectDigest.id}`;

            // fill the decimal inputs
            accum[key] = score.toFixed(1).split('.').map(Number);
          }
        });

        return accum;
      }, {});

      // update the UI for the decimal inputs
      this.set('serviceRatings', {
        ...currentServiceRatings,
        ...loadedDigestServiceRatings,
      });
    }
  }

  @dropTask
  *saveServiceRatingsTask(user, serviceRatings, allServices) {
    // format for request to include all services
    const allServiceRatings = {};
    allServices.forEach((service) => {
      allServiceRatings[service.id] = serviceRatings[service.id] ?? 0;
    });

    try {
      yield this.userCommandService.setServiceRatings(user, allServiceRatings);

      // navigate back to the needs vetting search
      this.router.transitionTo(EMBER_ROUTE_NAMES.ADMIN__USERS, {
        queryParams: {
          type: USER.TYPES.SELLER,
          vetting: VETTING_STATUS_NEEDS_VETTING,
        }
      });
    } catch (e) {
      this.errorTracker.error(e, this, 'Failed to save service rating on a user');
      this.applicationStateManager.addToast('Failed to rate user. Please reload the page and try again.');
    }
  }

  @task
  *refreshProjectdigestTask(projectdigestId) {
    try {
      const response = yield this.projectDigestCommandService.getProjectDigest(projectdigestId, ['projectdigest-verifications:verified_or_latest']);
      this.shData.pushResponseToStore(response);
    } catch (error) {
      this.errorTracker.error('refreshProjectdigestTask - failed to reload projectdigest', this, error);
      this.applicationStateManager.addToast('There was a problem refreshing project data. Please reload the page and try again.');
    }
  }

  @task
  *setBadLinkTask(projectdigestId) {
    try {
      yield this.projectDigestCommandService.setBadLink(projectdigestId, true);
      yield this.refreshProjectdigestTask.perform(projectdigestId);
    } catch(error) {
      this.errorTracker.error('setBadLinkTask - failed to set bad link or reload projectdigest', this, error);
      this.applicationStateManager.addToast('There was a problem setting bad link status. Please reload the page and try again.');
    }
  }

  @task
  *setVerificationStatusTask(projectdigestVerificationId, projectdigestId, status) {
    try {
      yield this.projectDigestCommandService.setVerificationStatus(projectdigestVerificationId, status);
      yield this.refreshProjectdigestTask.perform(projectdigestId);
    } catch (error) {
      this.errorTracker.error('setVerificationStatusTask - failed to set verification status', this, error);
      this.applicationStateManager.addToast('There was a problem setting verification status. Please reload the page and try again.');
    }
  }

  @task
  *saveAdminNoteTask(projectDigest, noteText) {
    try {
      yield this.noteCommandService.saveAdminNote(projectDigest, noteText);
    } catch (e) {
      this.errorTracker.error(e, this, 'Failed to save admin note');
      this.applicationStateManager.addToast("Failed to save admin's note. Please reload the page and try again.");
    }
  }

  @task
  *onVerifyProjectDigestTask(projectDigest) {
    try {
      yield this.projectDigestCommandService.setProjectDigestVerification(projectDigest, true);
      this.applicationStateManager.addToast('Project verified');
    } catch (e) {
      this.errorTracker.error(e, this, 'Failed to verify project');
      this.applicationStateManager.addToast('Failed to verify project digest. Please reload the page and try again.');
    }
  }

  @enqueueTask
  *invokeMenu(user, menuAction) {
    switch (menuAction) {
      case USERS_LIST_COMPONENT.MENU_ACTIONS.TOGGLE_APPROVED:
        yield this.userCommandService.toggleApproval(user);
        yield this.reloadUser.perform(user);
        break;
    }
  }

  @task
  *reloadUser(user) {
    return this.store.findRecord('user', user.id, {
      reload: true,
      include: VET_USER_INCLUDES.join(','),
    });
  }

  @task
  *loadClassificationsTask() {
    try {
      return yield this.classificationCommandService.loadClassifications({
        filters: {
          types: [
            CLASSIFICATION_TYPES.TYPE_DEFAULT,
            CLASSIFICATION_TYPES.TYPE_ADMIN_ONLY,
          ],
        },
      });
    } catch (error) {
      this.errorTracker.error('loadClassificationsTask - failed to fetch classifications', this, error);
      this.applicationStateManager.addToast("Failed to load classifications. Please try again.");
    }
  }

  @enqueueTask
  *updateProjectdigestClassifications(classificationIds, projectdigestId) {
    try {
      return yield this.projectDigestCommandService.updateProjectdigestClassifications(projectdigestId, classificationIds)
    } catch (error) {
      this.errorTracker.error(`updateProjectdigestClassifications - failed to update classifications for projectdigest: ${projectdigestId}`, this, error);
      this.applicationStateManager.addToast("Failed to update classifications for project. Please try again.");
    }
  }

  @enqueueTask
  *updateProjectdigestFeatured(featured, projectdigestId) {
    try {
      return yield this.projectDigestCommandService.updateProjectdigestFeaturedStatus(projectdigestId, featured);
    } catch (error) {
      this.errorTracker.error(`updateProjectdigestFeatured - failed to update featured status for projectdigest: ${projectdigestId}`, this, error);
      this.applicationStateManager.addToast("Failed to update featured status for project. Please try again.");
    }
  }

  @action
  handleUnlistedServiceSelected(service) {
    this.set('selectedUnlistedService', service);
  }

  @action
  handleGetThumbnail(link) {
    return isUrl(link) ? this.videoThumbnailService.getThumb(link) : null;
  }

  /**
   * Store the service rating input. A service may be rated under multiple digests.
   * Services with multiple ratings will be averaged.
   *
   * @param {String} name of variable to store data in
   * @param {Number} serviceId
   * @param {Number} inputField 0 for whole, 1 for decimal
   * @param {Number} rating
   * @param {Number} digestId
   */
  @action
  setServiceRating(name, serviceId, inputField, rating, digestId = null) {
    const key = digestId ? `${serviceId}-${digestId}` : serviceId;
    const inputFields = {
      ...this[name][key],
      [inputField]: rating, // update changed
    };
    this.set(name, {...this[name], [key]: inputFields});
  }

  /**
   * Clear a service rating input
   *
   * @param {*} name of variable to clear the data from
   * @param {*} serviceId
   * @param {*} digestId
   */
  @action
  clearServiceRating(name, serviceId, digestId = null) {
    const key = digestId ? `${serviceId}-${digestId}` : serviceId;
    const currentRatings = { ...this[name] };
    delete currentRatings[key];
    this.set(name, currentRatings);
  }

  @action
  handleAddToast(message) {
    this.applicationStateManager.addToast(message);
  }

  @action
  handleOpenVideoPlayer(projectDigest) {
    showModal(
      this,
      LightboxOverlay,
      {
        digestsToShowForVideoCarousel: [projectDigest],
      }
    );
  }

  @action
  handleOpenVerificationModal(projectDigest) {
    showModal(
      this,
      'admin/verify-project-digest-modal',
      {
        projectDigest,
        verifyTask: this.onVerifyProjectDigestTask,
        saveAdminNote: this.saveAdminNoteTask,
      }
    );
  }

  /**
   * Add an unlisted service to the UI to allow rating
   */
  @action
  handleAddUnlistedService() {
    const { selectedUnlistedService } = this;
    if (!selectedUnlistedService) return;

    this.setProperties({
      adminAddedUnlistedServices: [
        ...this.adminAddedUnlistedServices,
        selectedUnlistedService,
      ],
      selectedUnlistedService: null,
    });
  }

  /**
   * Remove an unlisted service from the UI along with the rating
   */
  @action
  handleRemoveUnlistedService(serviceToRemoveId) {
    // remove score from stored ratings
    const updatedServiceRatings = { ...this.serviceRatings };
    delete updatedServiceRatings[serviceToRemoveId];

    // remove from list of manually added services
    const updatedUnlistedServices = [...this.adminAddedUnlistedServices].filter(({id}) => serviceToRemoveId != id);

    this.setProperties({
      adminAddedUnlistedServices: updatedUnlistedServices,
      serviceRatings: updatedServiceRatings,
    });
  }

  @action
  setFeaturedChanged(newValue, projectdigestId) {
    this.updateProjectdigestFeatured.perform(newValue, projectdigestId);
  }

  @action
  setClassificationsChanged(newValues, projectdigestId) {
    const classificationIds = getClassificationIdsFromObject(newValues);
    this.updateProjectdigestClassifications.perform(classificationIds, projectdigestId);
  }
}

export default AdminUserRateController;
